import styled, { css, CSSProperties } from 'styled-components';

import { Box, Spinner } from '../atoms';
import Icon, { AvailableIcons } from '../atoms/icons/Icon';
import { textStyles } from '../atoms/Texts';
import { colors } from '../utils/colors';

export type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'danger'
    | 'ghost';

export type CommonButtonStyles = CSSProperties;

export const commonButtonStyles: CommonButtonStyles = {
    gap: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '500',
    fontSize: '1rem',
    lineHeight: '1.5rem',
    borderRadius: '0.75rem',
    padding: '0.75rem 1rem',
    border: 'none',
    color: colors.black,
    transition: 'all 200ms',
} as const satisfies CommonButtonStyles;

export const standardButtonStylesAcrossAllCompanies = css`
    ${textStyles({ variant: 'button' })}
    align-self: flex-start;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    overflow: hidden;
`;

export type ButtonBaseStyles = {
    $variant: ButtonVariant;
    $isSelected?: boolean;
    disabled?: boolean;
};

export const buttonBaseStyles = css<ButtonBaseStyles>`
    ${(props) => props.theme.button[props.$variant]}

    ${standardButtonStylesAcrossAllCompanies}
    ${(props) => {
        const variantStyles = props.theme.button[props.$variant];

        if (props.disabled) {
            return css`
                ${variantStyles.disabled}
                cursor: not-allowed;
            `;
        }
        if (props.$isSelected) {
            return css`
                ${variantStyles.active};
            `;
        }
        return css`
            ${variantStyles.default};
            &:hover {
                ${variantStyles.hover};
            }
            &:focus-visible {
                ${variantStyles.focus};
            }
            &:active {
                ${variantStyles.active};
            }
        `;
    }};
`;

type ButtonStyleType = {
    default: CSSProperties;
    hover: CSSProperties;
    focus: CSSProperties;
    disabled: CSSProperties;
    active: CSSProperties & { hover?: CSSProperties };
} & CommonButtonStyles;

const commonButtonStates = {
    hover: {
        outline: `0rem solid ${colors.blue800}`,
        backgroundColor: colors.blue800,
        color: colors.white,
    },
    active: {
        outline: `0.125rem solid ${colors.blue800}`,
        backgroundColor: colors.blue800,
        color: colors.white,
    },
    focus: {
        outline: `0.25rem solid ${colors.indigo400}`,
    },
};

export const defaultButtonStyle: Record<ButtonVariant, ButtonStyleType> = {
    primary: {
        ...commonButtonStyles,
        default: {
            backgroundColor: colors.blue900,
            color: colors.white,
        },
        hover: commonButtonStates.hover,
        focus: commonButtonStates.focus,
        active: commonButtonStates.active,
        disabled: {
            backgroundColor: colors.greyscaleSurfaceDisabled,
            color: colors.greyscaleTextDisabled,
        },
    },
    secondary: {
        ...commonButtonStyles,
        default: {
            backgroundColor: colors.blue50,
        },
        hover: {
            backgroundColor: colors.blue100,
        },
        focus: {
            outline: `0.25rem solid ${colors.indigo400}`,
            backgroundColor: colors.blue50,
        },
        active: {
            backgroundColor: colors.blue100,
            outline: `0.25rem solid ${colors.blue100}`,
        },
        disabled: {
            backgroundColor: colors.greyscaleSurfaceDisabled,
            color: colors.greyscaleTextDisabled,
        },
    },
    tertiary: {
        ...commonButtonStyles,
        default: {
            outline: `0.0625rem solid ${colors.blue900}`,
            backgroundColor: colors.transparent,
            color: colors.blue900,
        },
        hover: commonButtonStates.hover,
        active: commonButtonStates.active,
        focus: commonButtonStates.focus,

        disabled: {
            outline: `0.25rem solid ${colors.greyscaleBorderDisabled}`,
            backgroundColor: colors.transparent,
            color: colors.greyscaleTextDisabled,
        },
    },
    ghost: {
        ...commonButtonStyles,
        default: {
            backgroundColor: colors.transparent,
            color: colors.blue900,
        },
        hover: {
            backgroundColor: colors.blue100,
        },
        focus: commonButtonStates.focus,
        active: {
            outline: `0.125rem solid ${colors.blue100}`,
            backgroundColor: colors.blue100,
        },
        disabled: {
            backgroundColor: colors.transparent,
            color: colors.greyscaleTextDisabled,
        },
    },
    danger: {
        ...commonButtonStyles,
        default: {
            backgroundColor: colors.red50,
            color: colors.red700,
        },
        hover: { backgroundColor: colors.red100 },
        focus: {
            outline: `0.25rem solid ${colors.indigo400}`,
        },
        active: {
            backgroundColor: colors.red700,
            color: colors.white,
        },
        disabled: {
            backgroundColor: colors.greyscaleSurfaceDisabled,
            color: colors.greyscaleTextDisabled,
        },
    },
};

export const ButtonStyle = styled.button<{
    $variant: ButtonVariant;
    $isSelected?: boolean;
    disabled?: boolean;
}>`
    ${buttonBaseStyles}
`;

type TextChildren = {
    children: string;
    ariaLabel?: never;
};

type NonTextChildren = {
    children: React.ReactNode;
    ariaLabel: string;
};

export type ButtonProps = React.PropsWithChildren<{
    className?: string;
    loadingLabel?: string;
    iconLeft?: AvailableIcons;
    iconRight?: AvailableIcons;
    variant?: ButtonVariant;
    isSelected?: boolean;
    disabled?: boolean;
    onClick?: () => void;
    excludeFromTabOrder?: boolean;
    isLoading?: boolean;
    type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
}> &
    (TextChildren | NonTextChildren);

export const Button = ({
    children,
    iconLeft,
    iconRight,
    variant = 'primary',
    isSelected = false,
    disabled = false,
    onClick,
    ariaLabel,
    excludeFromTabOrder,
    isLoading,
    type,
    className,
    loadingLabel,
}: ButtonProps) => {
    return (
        <ButtonStyle
            className={className}
            type={type}
            tabIndex={excludeFromTabOrder ? -1 : undefined}
            aria-label={ariaLabel}
            onClick={onClick}
            $variant={variant}
            $isSelected={isSelected}
            disabled={disabled || isLoading}
        >
            <Box as="span" gap="8">
                {iconLeft ? (
                    isLoading ? (
                        <Spinner loadingLabel={loadingLabel} />
                    ) : (
                        <Icon name={iconLeft} />
                    )
                ) : null}

                {children}
                {iconRight ? (
                    isLoading ? (
                        <Spinner loadingLabel={loadingLabel} />
                    ) : (
                        <Icon name={iconRight} />
                    )
                ) : null}
                {!iconLeft && !iconRight && isLoading ? (
                    <Spinner loadingLabel={loadingLabel} />
                ) : null}
            </Box>
        </ButtonStyle>
    );
};
